const sheet = new CSSStyleSheet();
sheet.replaceSync(`
  :host {
    position: fixed;
    z-index: -1000;
    opacity: 0;
    inset: 0;
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow-y: scroll;
    overflow-x: hidden;
    background: rgba(0, 0, 0, 0.7);
    visibility: hidden;
    transition: z-index 0.2s step-end, opacity 0.2s linear, visibility 0.3s ease-in-out;
  }
  :host([type="top"]) {
    justify-content: flex-end;
    align-items: flex-end;
  }
  :host([type="bottom"]) {
    align-items: flex-start;
  }
  :host([type="right"]) {
    right: 0;
    justify-content: flex-end;
    align-items: stretch;
  }
  :host([type="left"]) {
    left: 0;
    justify-content: flex-start;
    align-items: stretch;
  }
  :host([open]) {
    display: flex;
    z-index: 100;
    opacity: 1;
    visibility: visible;
    transition: z-index 0.4s step-start, opacity 0.2s linear, visibility 0.2s ease-in-out;
  }
  :host > ::slotted(div) {
    position: relative;
    display: flex;
    flex-direction: column;
    overflow-y: auto;
    background: #fff;
    border-top-width: 1px;
    border-color: #c4c4c4;
    transition: transform 0.4s;
  }
  :host([type="top"]) > ::slotted(div) {
    transform: translateY(200%);
    box-shadow: -10px -10px 5px 2px rgb(0 0 0 / 10%);
  }
  :host([type="bottom"]) > ::slotted(div) {
    transform: translateY(-200%);
    box-shadow: 0 10px 5px 2px rgb(0 0 0 / 10%);
  }
  :host([type="top"][open]) > ::slotted(div),
  :host([type="bottom"][open]) > ::slotted(div) {
    transform: translateY(0);
  }
  :host([type="right"]) > ::slotted(div) {
    transform: translateX(200%);
    box-shadow: -10px 0 5px 2px rgb(0 0 0 / 10%);
  }
  :host([type="left"]) > ::slotted(div) {
    transform: translateX(-200%);
    box-shadow: 10px 0 5px 2px rgb(0 0 0 / 10%);
  }
  :host([type="right"][open]) > ::slotted(div),
  :host([type="left"][open]) > ::slotted(div) {
    transform: translateX(0);
  }
`);

/**
 * Lab Modal Custom Element
 * Modal/drawer component with backdrop, animations, and accessibility
 * Supports top, bottom, left, right positioning via 'type' attribute
 *
 * @example
 * <lab-modal id="cart" type="right">
 *   <div>Modal content</div>
 * </lab-modal>
 */
class LabModal extends HTMLElement {
  /** @type {HTMLElement|null} Element that had focus before modal opened */
  #previousFocus = null;

  /** @type {Function|null} Bound focus trap handler */
  #boundTrapFocus = null;

  /** `@type` {boolean} Internal open state guard */
  #isOpen = false;

  constructor() {
    super();

    // Set initial ARIA state
    if (!this.hasAttribute('aria-hidden')) {
      this.setAttribute('aria-hidden', 'true');
    }
    if (!this.hasAttribute('role')) {
      this.setAttribute('role', 'dialog');
    }
    if (!this.hasAttribute('aria-modal')) {
      this.setAttribute('aria-modal', 'true');
    }

    this.modalId = this.id;
    this.boundHandleEscape = null;

    // Create shadow DOM
    this.attachShadow({mode: 'open'});
    this.shadowRoot.adoptedStyleSheets = [sheet];
    this.shadowRoot.innerHTML = `<slot></slot>`;
  }

  connectedCallback() {
    this.registerModal();

    // Click outside to close
    this.boundHandleModalClick = this.handleModalClick.bind(this);
    this.addEventListener('click', this.boundHandleModalClick, {passive: true});

    // Escape key to close
    this.boundHandleEscape = this.handleEscapeKey.bind(this);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.boundHandleModalClick);

    // Remove escape key listener
    if (this.boundHandleEscape) {
      document.removeEventListener('keydown', this.boundHandleEscape);
      this.boundHandleEscape = null;
    }

    // Remove focus trap listener
    if (this.#boundTrapFocus) {
      this.removeEventListener('keydown', this.#boundTrapFocus);
      this.#boundTrapFocus = null;
    }

    // Clean up focus reference
    this.#previousFocus = null;

    // Clean up from global modals registry
    if (window.theme?.modals && this.modalId) {
      delete window.theme.modals[this.modalId];
    }
  }

  static get observedAttributes() {
    return ['open'];
  }

  /**
   * Handle attribute changes
   * @param {string} name - Attribute name
   * @param {string|null} oldValue - Previous value
   * @param {string|null} newValue - New value
   */
  attributeChangedCallback(name, oldValue, newValue) {
    if (name !== 'open') return;

    // Attribute added (oldValue null -> newValue '')
    if (oldValue === null && newValue !== null) {
      this.openModal();
    }
    // Attribute removed (oldValue '' -> newValue null)
    else if (oldValue !== null && newValue === null) {
      this.closeModal();
    }
  }

  /**
   * Register modal in global theme.modals registry for external access
   */
  registerModal() {
    if (!this.modalId) {
      // eslint-disable-next-line no-console
      console.warn('LabModal: Modal must have an id attribute');
      return;
    }

    this.openModal = this.openModal.bind(this);
    this.closeModal = this.closeModal.bind(this);

    // Initialize modals object if needed
    if (!window.theme) window.theme = {};
    if (!window.theme.modals) window.theme.modals = {};

    window.theme.modals[this.modalId] = {
      open: this.openModal,
      close: this.closeModal,
    };
  }

  /**
   * Open modal with accessibility features
   */
  openModal() {
    if (this.#isOpen) return;
    this.#isOpen = true;

    // Store the element that triggered the modal for focus restoration
    this.#previousFocus = document.activeElement;

    if (!this.hasAttribute('open')) {
      this.setAttribute('open', '');
    }
    this.setAttribute('aria-hidden', 'false');

    // Lock body scroll
    LabModal.lockBodyScroll();

    // Focus first focusable element inside modal (usually close button)
    requestAnimationFrame(() => {
      const focusableSelector =
        'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
      const firstFocusable = this.querySelector(focusableSelector);

      if (firstFocusable) {
        firstFocusable.focus();
      } else {
        // Fallback: make modal itself focusable
        this.setAttribute('tabindex', '-1');
        this.focus();
      }
    });

    // Add Escape key listener
    if (this.boundHandleEscape) {
      document.addEventListener('keydown', this.boundHandleEscape);
    }

    // Add focus trap listener
    this.#boundTrapFocus = this.#trapFocus.bind(this);
    this.addEventListener('keydown', this.#boundTrapFocus);
  }

  /**
   * Close modal and restore scroll
   */
  closeModal() {
    if (!this.#isOpen) return;
    this.#isOpen = false;
    if (this.hasAttribute('open')) {
      this.removeAttribute('open');
    }

    this.setAttribute('aria-hidden', 'true');

    // Unlock body scroll
    LabModal.unlockBodyScroll();

    // Remove Escape key listener
    if (this.boundHandleEscape) {
      document.removeEventListener('keydown', this.boundHandleEscape);
    }

    // Remove focus trap listener
    if (this.#boundTrapFocus) {
      this.removeEventListener('keydown', this.#boundTrapFocus);
      this.#boundTrapFocus = null;
    }

    // Return focus to the element that triggered the modal
    if (this.#previousFocus && typeof this.#previousFocus.focus === 'function') {
      this.#previousFocus.focus();
    }
    this.#previousFocus = null;
  }

  /**
   * Handle Escape key to close modal
   * @param {KeyboardEvent} event - Keyboard event
   */
  handleEscapeKey(event) {
    if (!event || event.key !== 'Escape') return;
    if (!this.hasAttribute('open')) return;

    this.closeModal();
  }

  /**
   * Handle backdrop click to close modal
   * @param {MouseEvent} event - Click event
   */
  handleModalClick(event) {
    event.stopPropagation();

    // Only close if clicking backdrop (not modal content)
    if (event.target !== this) return;

    this.closeModal();
  }

  /**
   * Trap focus within modal (Tab/Shift+Tab cycling)
   * Prevents users from tabbing out of the modal into background content
   * @param {KeyboardEvent} event - Keyboard event
   */
  #trapFocus(event) {
    if (event.key !== 'Tab') return;

    const focusableSelector =
      'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
    const focusable = this.querySelectorAll(focusableSelector);

    if (focusable.length === 0) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      // Shift+Tab from first element → go to last
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      // Tab from last element → go to first
      event.preventDefault();
      first.focus();
    }
  }

  /**
   * Prevent body scrolling when modal is open
   */
  static lockBodyScroll() {
    document.body.classList.add('lock-scroll');
  }

  /**
   * Restore body scrolling when modal is closed
   */
  static unlockBodyScroll() {
    document.body.classList.remove('lock-scroll');
  }
}

// Only define the custom element if it hasn't been defined already
if (!customElements.get('lab-modal')) {
  customElements.define('lab-modal', LabModal);
}
